import { z } from 'zod'
import baseQuery from './base-query.js'
import { createApi } from '@reduxjs/toolkit/query/react'

export const workflowInputSchema = z.object({
  /** The userId the job is running for. */
  userId: z.string(),
  /** Present and non-empty when the job exported one space rather than the account (spec §4) */
  rootEntityId: z.string().describe('The subtree this export was rooted at').optional(),
})
export type WorkflowInput = z.infer<typeof workflowInputSchema>

export const workflowOutputSchema = workflowInputSchema.extend({
  name: z.string(),
  status: z.enum(['ABORTED', 'FAILED', 'RUNNING', 'SUCCEEDED', 'TIMED_OUT', 'PENDING_REDRIVE']),
  startDate: z.iso.datetime(),
  stopDate: z.iso.datetime(),
  /** Extend this */
  input: workflowInputSchema.loose(),
  output: z.unknown(),
})
export type WorkflowOutput = z.infer<typeof workflowOutputSchema>

export const exportWorkflowOutputSchema = workflowOutputSchema.extend({
  // The signed S3 URL is only populated when the workflow has SUCCEEDED.
  // RUNNING / FAILED / etc. carry `url: null` over the wire.
  url: z.string().nullable(),
  /** Step Functions' short error code for a terminal failure (spec 2026-09-09 §6) */
  error: z.string().describe('Step Functions error code').nullable().optional(),
  /** The raw cause — ECS JSON; support material, never rendered to the user */
  cause: z.string().describe('Step Functions failure cause').nullable().optional(),
})
export type ExportWorkflowOutput = z.infer<typeof exportWorkflowOutputSchema>

// Define a service using a base URL and expected endpoints
export const workflowsApi = createApi({
  reducerPath: 'workflows-api',
  baseQuery,
  tagTypes: ['ExportWorkflows', 'ImportEnexWorkflows'],
  endpoints: builder => ({
    export: builder.mutation<void, { rootEntityId?: string } | void>({
      invalidatesTags: ['ExportWorkflows'],
      query: params => ({
        method: 'POST',
        url: `workflows/export`,
        body: params ?? {},
      }),
    }),
    importEnex: builder.mutation<
      void,
      {
        name: string
        parentId?: string
        enexStorageKey: string
      }
    >({
      invalidatesTags: ['ImportEnexWorkflows'],
      query: params => ({
        method: 'POST',
        url: `workflows/import-enex`,
        body: params,
      }),
    }),
    getExports: builder.query<ExportWorkflowOutput[], void>({
      providesTags: ['ExportWorkflows'],
      query: () => `workflows/export`,
    }),
  }),
})
